import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { machineService } from '../services/api';

const ScreenMirrorModal = ({ show, onHide, machine }) => {
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(2000); // default 2s
  const [zoomLevel, setZoomLevel] = useState('fit'); // 'fit' or 25, 50, 75, 100, 125, 150, 175, 200

  const handleZoomIn = () => {
    if (zoomLevel === 'fit') {
      setZoomLevel(100);
    } else {
      setZoomLevel(prev => Math.min(200, prev + 25));
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel === 'fit') {
      setZoomLevel(75);
    } else {
      setZoomLevel(prev => Math.max(25, prev - 25));
    }
  };

  const timerRef = useRef(null);
  const currentUrlRef = useRef(null);

  // Clean up ObjectURL to prevent memory leaks
  const cleanUpUrl = () => {
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current);
      currentUrlRef.current = null;
    }
  };

  const fetchScreenshot = async () => {
    if (!machine) return;
    try {
      setError(null);
      const blob = await machineService.getScreenshot(machine.id);

      cleanUpUrl();
      const objectUrl = URL.createObjectURL(blob);
      currentUrlRef.current = objectUrl;
      setScreenshotUrl(objectUrl);
    } catch (err) {
      console.error('Screenshot fetch error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to capture screenshot. Make sure the LANtern Service agent is running and connected on the target workstation.');
      // Automatically pause on error to avoid spamming requests
      setIsPlaying(false);
    } finally {
      setLoading(false);
    }
  };

  // Trigger initial fetch when modal opens
  useEffect(() => {
    if (show && machine) {
      setLoading(true);
      setScreenshotUrl(null);
      setError(null);
      setIsPlaying(true);
      fetchScreenshot();
    } else {
      cleanUpUrl();
      setScreenshotUrl(null);
    }
  }, [show, machine]);

  // Set up polling interval
  useEffect(() => {
    if (show && machine && isPlaying && !loading) {
      timerRef.current = setInterval(() => {
        fetchScreenshot();
      }, refreshInterval);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [show, machine, isPlaying, refreshInterval, loading]);

  const handleTogglePlay = () => {
    setIsPlaying(prev => !prev);
  };

  const handleManualRefresh = () => {
    setLoading(true);
    fetchScreenshot();
  };

  const handleClose = () => {
    cleanUpUrl();
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      scrollable
      contentClassName="modal-content-premium"
    >
      <Modal.Header closeButton className="modal-header-premium border-0">
          <Modal.Title style={{ fontWeight: 700 }} className="text-theme-main">
            <i className="fas fa-tv me-2 text-primary"></i>
            Live Screen Mirror: {machine?.name} ({machine?.ip_address})
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-theme-dark p-0 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '250px', backgroundColor: '#0f172a' }}>
          {error && (
            <div className="p-4 w-100">
              <Alert variant="danger" className="border-0 mb-0 shadow-sm" style={{ backgroundColor: 'rgba(244, 63, 94, 0.15)', color: '#f43f5e' }}>
                <i className="fas fa-exclamation-circle me-2"></i>
                <strong>Screen Capture Failed:</strong> {error}
                <hr />
                <p className="mb-0 fs-7">
                  Ensure the target Windows workstation has:
                  <br />• The <strong>LANtern Service agent</strong> running and connected.
                  <br />• An <strong>active, interactive desktop user session</strong> (screen not disconnected).
                </p>
              </Alert>
            </div>
          )}

          {loading && !screenshotUrl && (
            <div className="text-center py-5 text-theme-muted">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p>Establishing remote session and capturing screen...</p>
            </div>
          )}

          {screenshotUrl ? (
            <div 
              className={`position-relative w-100 overflow-auto ${zoomLevel === 'fit' ? 'd-flex align-items-center justify-content-center' : ''}`}
              style={{ maxHeight: '60vh', minHeight: '250px', width: '100%', textAlign: 'center' }}
            >
              <img
                src={screenshotUrl}
                alt={`Remote Desktop screen of ${machine?.name}`}
                className="border border-dark rounded shadow-lg"
                style={
                  zoomLevel === 'fit'
                    ? {
                        maxHeight: '60vh',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        backgroundColor: '#000'
                      }
                    : {
                        width: `${zoomLevel}%`,
                        height: 'auto',
                        margin: '0 auto',
                        display: 'block',
                        backgroundColor: '#000',
                        transition: 'width 0.15s ease'
                      }
                }
              />

              {loading && (
                <div className="position-absolute top-2 right-2 bg-dark bg-opacity-75 rounded px-2 py-1 text-white fs-7 d-flex align-items-center gap-2">
                  <Spinner size="sm" animation="border" variant="light" />
                  Refreshing...
                </div>
              )}
            </div>
          ) : (
            !loading && !error && (
              <div className="text-center py-5 text-theme-muted">
                <i className="fas fa-tv fa-3x mb-3 text-muted"></i>
                <p>No screenshot data available.</p>
              </div>
            )
          )}
        </Modal.Body>

        <Modal.Footer className="modal-footer-premium border-0 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            {/* Play/Pause Button */}
            <Button
              variant={isPlaying ? 'premium-secondary' : 'premium-primary'}
              onClick={handleTogglePlay}
              disabled={!!error}
              className="d-flex align-items-center gap-2 py-2 px-3"
            >
              <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
              {isPlaying ? 'Pause Mirroring' : 'Resume Mirroring'}
            </Button>

            {/* Manual Refresh */}
            <Button
              variant="premium-secondary"
              onClick={handleManualRefresh}
              disabled={loading}
              className="d-flex align-items-center gap-2 py-2 px-3"
            >
              <i className="fas fa-sync"></i>
              Refresh Now
            </Button>

            {/* Status indicator */}
            <span className="fs-7 d-flex align-items-center gap-1.5">
              <span className={`pulse-dot ${isPlaying ? 'bg-success' : 'bg-warning'}`}></span>
              {isPlaying ? 'Live Streaming' : 'Paused'}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* Zoom Controls */}
            <div className="d-flex align-items-center gap-1.5 me-3">
              <span className="text-theme-muted fs-7 me-1">Zoom:</span>
              <Button
                variant="premium-secondary"
                size="sm"
                className="py-1 px-2"
                onClick={handleZoomOut}
                disabled={zoomLevel === 25 || !screenshotUrl}
                title="Zoom Out"
              >
                <i className="fas fa-search-minus"></i>
              </Button>
              <span className="text-theme-main fs-7 font-semibold text-center" style={{ width: '45px' }}>
                {zoomLevel === 'fit' ? 'Fit' : `${zoomLevel}%`}
              </span>
              <Button
                variant="premium-secondary"
                size="sm"
                className="py-1 px-2"
                onClick={handleZoomIn}
                disabled={zoomLevel === 200 || !screenshotUrl}
                title="Zoom In"
              >
                <i className="fas fa-search-plus"></i>
              </Button>
              {zoomLevel !== 'fit' && (
                <Button
                  variant="premium-secondary"
                  size="sm"
                  className="py-1 px-2 fs-7"
                  onClick={() => setZoomLevel('fit')}
                  title="Fit to Screen"
                >
                  Reset
                </Button>
              )}
            </div>

            {/* Polling Interval Select */}
            <Form.Group className="d-flex align-items-center gap-2 mb-0">
              <Form.Label className="text-theme-muted mb-0 fs-7 text-nowrap">Refresh Rate:</Form.Label>
              <Form.Select
                size="sm"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="form-input-premium py-1"
                style={{ width: '120px' }}
                disabled={!isPlaying}
              >
                <option value={1000}>1 Second</option>
                <option value={2000}>2 Seconds</option>
                <option value={5000}>5 Seconds</option>
                <option value={10000}>10 Seconds</option>
              </Form.Select>
            </Form.Group>

            <Button variant="premium-secondary" onClick={handleClose} className="ms-2">
              Close
            </Button>
          </div>
        </Modal.Footer>
    </Modal>
  );
};

export default ScreenMirrorModal;
